import type { V2PlanInfo } from "schemas/billing";
import type {
  BillingInterval,
  SubscriptionDetails,
  V2BillingTier,
} from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import * as React from "react";

import { useModal } from "@editor/hooks/useModal";
import { workspaceRouter } from "@editor/trpc/workspace";

import Separator from "@replo/design-system/components/Separator";
import { Card } from "@replo/design-system/components/shadcn/core/card";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { pluralize } from "replo-utils/lib/pluralize";
import { getV2BillingPlanInfoByTier } from "schemas/billing";

import PlanCardButton from "../shared/PlanCardButton";
import QuantityControl from "../shared/QuantityControl";
import {
  getCountOfV2AdditionalShopsInSubscription,
  getV2PlanFeatures,
  V2PlanHeader,
} from "./V2BillingUtils";
import { V2PlanPricePerMonthIncludingAllProducts } from "./V2PlanPrice";

const V2PlanCard = ({
  plan,
  currentTier,
  isYearly,
  workspace,
  isCustomPlan,
  subscriptionDetails,
  isDowngrade,
  isCurrentTier,
  billingInterval,
  isYearlyIntervalUpgrade,
  isSubscriptionDetailsPending,
  isViewOnly,
}: {
  plan: V2PlanInfo;
  currentTier: V2BillingTier;
  isYearly: boolean;
  workspace: Workspace;
  isCustomPlan: boolean;
  subscriptionDetails?: SubscriptionDetails;
  isSubscriptionDetailsPending: boolean;
  isDowngrade: boolean;
  isCurrentTier: boolean;
  isYearlyIntervalUpgrade: boolean;
  billingInterval: BillingInterval;
  isViewOnly: boolean;
}) => {
  const modal = useModal();

  const currentV2AdditionalShops = React.useMemo(() => {
    if (!subscriptionDetails) {
      return 0;
    }

    return getCountOfV2AdditionalShopsInSubscription(subscriptionDetails);
  }, [subscriptionDetails]);

  const [v2AdditionalShops, setV2AdditionalShops] = React.useState(
    currentV2AdditionalShops,
  );

  const { data: v2ShopsUsage } = workspaceRouter.getV2ShopsUsage.useQuery(
    !workspace?.id || isCustomPlan ? skipToken : workspace.id,
  );

  const hasConfigurationChanges =
    v2AdditionalShops !== currentV2AdditionalShops || isYearlyIntervalUpgrade;

  const handleShopsIncrement = () => {
    setV2AdditionalShops((prev) => prev + 1);
  };

  const handleShopsDecrement = () => {
    setV2AdditionalShops((prev) => Math.max(0, prev - 1));
  };

  const isPlanConfigurationValidWithTooltipContent = React.useMemo(() => {
    if (isCustomPlan || !v2ShopsUsage) {
      return {
        isPlanConfigurationValid: true,
        tooltipContent: undefined,
      };
    }

    const planInfo = getV2BillingPlanInfoByTier(plan.tier);

    const corePlanMaxV2Shops = planInfo.maxShops;

    // Null means unlimited
    if (corePlanMaxV2Shops !== null) {
      const totalV2ShopsAllowed = corePlanMaxV2Shops + v2AdditionalShops;
      if (v2ShopsUsage.current > totalV2ShopsAllowed) {
        return {
          isPlanConfigurationValid: false,
          tooltipContent: `You have ${v2ShopsUsage.current} shops, but your current plan configuration only allows ${totalV2ShopsAllowed}. You'll need to add more shops to continue.`,
        };
      }
    }

    return {
      isPlanConfigurationValid: true,
      tooltipContent: undefined,
    };
  }, [isCustomPlan, v2ShopsUsage, plan.tier, v2AdditionalShops]);

  const {
    isPlanConfigurationValid,
    tooltipContent: invalidPlanConfigurationTooltipContent,
  } = isPlanConfigurationValidWithTooltipContent;

  const submitButton = !isViewOnly && (
    <PlanCardButton
      isCurrentTier={isCurrentTier}
      hasConfigurationChanges={hasConfigurationChanges}
      isPlanConfigurationValid={isPlanConfigurationValid}
      invalidPlanConfigurationTooltipContent={
        invalidPlanConfigurationTooltipContent
      }
      isCustomPlan={isCustomPlan}
      isYearlyIntervalUpgrade={isYearlyIntervalUpgrade}
      isDowngrade={isDowngrade}
      currentPlan={currentTier}
      handleSubmitPlanChanges={() =>
        modal.openModal({
          type: "v2BillingUpgradeDowngradeModal",
          props: {
            newTier: plan.tier,
            newBillingInterval: billingInterval,
            newAdditionalShops: v2AdditionalShops,
          },
        })
      }
      isManagedByReplo={subscriptionDetails?.isManagedByReplo ?? false}
      billingInterval={billingInterval}
      workspace={workspace}
      isSubscriptionDetailsPending={isSubscriptionDetailsPending}
      isPlanCancelledButNotYetEnded={
        subscriptionDetails?.endedAt != null &&
        subscriptionDetails.endedAt > new Date()
      }
    />
  );

  return (
    <V2PlanCardShell
      plan={plan}
      isCustomPlan={isCustomPlan}
      isYearly={isYearly}
      v2AdditionalShops={v2AdditionalShops}
      handleShopsIncrement={handleShopsIncrement}
      handleShopsDecrement={handleShopsDecrement}
      isViewOnly={isViewOnly}
      submitButton={submitButton}
    />
  );
};

// NOTE (Cole 2025-09-25): My goal here is to make a UI shell that allows us to render these cards
// without needing logic around using them for plan modifications. Ex. the upgrade modal.
export const V2PlanCardShell = ({
  plan,
  isCustomPlan,
  isYearly,
  v2AdditionalShops,
  handleShopsIncrement,
  handleShopsDecrement,
  isViewOnly,
  submitButton,
}: {
  plan: V2PlanInfo;
  isCustomPlan: boolean;
  isYearly: boolean;
  v2AdditionalShops: number;
  handleShopsIncrement: () => void;
  handleShopsDecrement: () => void;
  isViewOnly: boolean;
  submitButton?: React.ReactNode;
}) => {
  const previousPlanTitle = exhaustiveSwitch({ type: plan.tier })({
    free: () => "You shouldn't see this",
    starter: () => "Free",
    pro: () => "Starter",
    custom_v2: () => "Pro",
  });

  const features = getV2PlanFeatures(plan.tier);

  return (
    <Card
      className={twMerge(
        "flex flex-col min-h-[500px] h-full overflow-hidden justify-between bg-sheet p-4",
        isViewOnly && "min-h-[400px]",
      )}
    >
      <div className="flex flex-col gap-5 justify-start h-40">
        <div className="flex flex-col gap-2">
          <V2PlanHeader tier={plan.tier} />
          <span className="text-sm text-muted">{plan.description}</span>
        </div>
        <div className="flex justify-between gap-1 items-center">
          {isCustomPlan ? (
            <span className="text-3xl font-semibold">Custom price</span>
          ) : (
            <>
              <V2PlanPricePerMonthIncludingAllProducts
                plan={plan}
                billingInterval={isYearly ? "yearly" : "monthly"}
                v2AdditionalShops={v2AdditionalShops}
                variant="header"
              />
              {isYearly && (
                <Tag size="sm" variant="v2" layoutClassName="h-fit">
                  Save 17% annually
                </Tag>
              )}
            </>
          )}
        </div>
        {!isCustomPlan && (
          <QuantityControl
            value={v2AdditionalShops}
            onIncrement={handleShopsIncrement}
            onDecrement={handleShopsDecrement}
            label={
              <span>
                <span className="font-bold">{v2AdditionalShops + 1}</span>{" "}
                {pluralize({
                  singular: "shop",
                  plural: "shops",
                  count: v2AdditionalShops + 1,
                })}
              </span>
            }
            isViewOnly={isViewOnly}
            minValue={0}
          />
        )}
      </div>
      <Separator variant="dashed" />
      <div className="flex flex-col gap-3">
        {plan.tier === "free" ? (
          <div className="text-sm font-medium  text-muted">Includes</div>
        ) : (
          <div className="text-sm font-medium  text-muted">
            Everything from {previousPlanTitle}, plus
          </div>
        )}

        {features.map((feature, index) => (
          <div key={index}>
            <div className="flex items-start gap-2 text-sm">
              <div className="flex-shrink-0">{feature.icon}</div>
              {feature.copy}
            </div>
          </div>
        ))}
      </div>
      {submitButton}
    </Card>
  );
};

export default V2PlanCard;
